/*
Author: Chathuraka Mallawa Arachchi
Date: 18/10/2024
Copyright © 2024 Infix!. All rights reserved.

Responsible for managing frm state including validations.
*/

import { DateRangeType } from '@/components/survey/SurveyModel/types/commonTypes'
import { TeamReviewOutcome } from '@/constants/constants'
import { Connection } from '@/utils'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { useDispatch } from 'react-redux'
import { updateWaitingListRecord } from './poaSurveySlice'

export type AnswerValueTypes = string | number | Date | DateRangeType[]
export type OpTypes = 'replace' | 'add' | 'remove'
export type pathTypes = '/InformationPackId'

export interface BasicAnswerSave {
    name: string
    value: AnswerValueTypes
}

export interface OutsideQuestionnaireAnswerSave {
    op: OpTypes
    path: pathTypes
    value: AnswerValueTypes
}

export interface IAnswerState {
    answerDetails: BasicAnswerSave[]
    answerRequest: boolean
    answersRequest: boolean
}

export interface InformationPack {
    id: number
    informationPackId: string
    title?: string
}

const initialState: IAnswerState = {
    answerDetails: [],
    answerRequest: false,
    answersRequest: false,
}

const patientAnswerSlice = createSlice({
    name: 'answers',
    initialState,
    reducers: {
        updateAnswer: (
            state,
            action: PayloadAction<{
                nodeAnswer: BasicAnswerSave
            }>
        ) => {
            const { nodeAnswer } = action.payload
            state.answerDetails = [...state.answerDetails, nodeAnswer]
        },
        updateAnswers: (
            state,
            action: PayloadAction<{
                nodeAnswers: BasicAnswerSave[]
            }>
        ) => {
            const { nodeAnswers } = action.payload
            state.answerDetails = [...state.answerDetails, ...nodeAnswers]
        },
        updateAnswerRequest: (
            state,
            action: PayloadAction<{
                value: boolean
            }>
        ) => {
            const { value } = action.payload
            state.answerRequest = value
        },
        updateAnswersRequest: (
            state,
            action: PayloadAction<{
                value: boolean
            }>
        ) => {
            const { value } = action.payload
            state.answersRequest = value
        },
        updateAnswersStatus: (
            state,
            action: PayloadAction<{
                value: boolean
            }>
        ) => {
            const { value } = action.payload
            state.answersRequest = value
        },
    },
})

export const {
    updateAnswer,
    updateAnswerRequest,
    updateAnswersRequest,
    updateAnswersStatus,
    updateAnswers,
} = patientAnswerSlice.actions

export const useUpdateAnswerSubmit = () => {
    const dispatch = useDispatch()

    const updateAnswerDispatch = async (
        field: string,
        value: AnswerValueTypes,
        id: number
    ) => {
        dispatch(updateAnswerRequest({ value: false }))
        try {
            // Call your API to get the answers
            const nodeAnswer: BasicAnswerSave = {
                name: field,
                value: value,
            }

            const answerPayload = {
                questionnaireResponseId: id,
                questionnaireAnswers: [nodeAnswer],
            }
            const response = await fetch(
                Connection.baseUrl('saveQuestionAnswer'),
                {
                    method: 'PATCH',
                    headers: Connection.headers(),
                    body: JSON.stringify(answerPayload),
                }
            )

            if (!response.ok) {
                throw new Error(
                    `Error: ${response.status} - ${response.statusText}`
                )
            }
            dispatch(updateAnswerRequest({ value: true }))
            dispatch(updateAnswer({ nodeAnswer }))
        } catch (error) {
            dispatch(updateAnswerRequest({ value: false }))
        }
    }

    return updateAnswerDispatch
}

export const useUpdateOutsideQuestionnaireAnswersDispatch = () => {
    const updateOutsideQuestionnaireAnswersDispatch = async (
        answers: OutsideQuestionnaireAnswerSave[],
        id: string
    ) => {
        try {
            const nodeAnswers: OutsideQuestionnaireAnswerSave[] = answers
            const url = Connection.baseUrl('WaitingList') + `/${id}`
            const response = await fetch(url, {
                method: 'PATCH',
                headers: Connection.headers(),
                body: JSON.stringify(nodeAnswers),
            })
            if (!response.ok) {
                alert('Unexpected error occured!')
                throw new Error(
                    `Error: ${response.status} - ${response.statusText}`
                )
            }
        } catch (error) {}
    }

    return updateOutsideQuestionnaireAnswersDispatch
}

export const useSetAnswerStatus = () => {
    const dispatch = useDispatch()

    const setAnswerStatus = async (value: boolean) => {
        dispatch(updateAnswersRequest({ value }))
    }

    return setAnswerStatus
}

export const useUpdateAnswersSubmit = () => {
    const dispatch = useDispatch()

    const updateAnswersDispatch = async (
        answers: BasicAnswerSave[],
        id: number
    ) => {
        dispatch(updateAnswersRequest({ value: false }))
        try {
            // Call your API to get the answers
            const nodeAnswers: BasicAnswerSave[] = answers

            const answerPayload = {
                questionnaireResponseId: id,
                questionnaireAnswers: nodeAnswers,
            }

            const TeamReviewOutcomeAnswer = answers.find(
                (answer) => answer.name === TeamReviewOutcome
            );
            // If TeamReviewOutcome is being updated, fetch the waiting list record
            if (TeamReviewOutcomeAnswer) {
                dispatch(updateWaitingListRecord({
                    teamReviewOutcome: TeamReviewOutcomeAnswer.value as string
                }));
            }

            const response = await fetch(
                Connection.baseUrl('saveQuestionAnswer'),
                {
                    method: 'PATCH',
                    headers: Connection.headers(),
                    body: JSON.stringify(answerPayload),
                }
            )

            if (!response.ok) {
                alert('Unexpected error occured!')
                throw new Error(
                    `Error: ${response.status} - ${response.statusText}`
                )
            }
            // Curent page should be set from here
            dispatch(updateAnswersRequest({ value: true }))
            dispatch(updateAnswers({ nodeAnswers }))
        } catch (error) {
            dispatch(updateAnswersRequest({ value: false }))
        }
    }

    return updateAnswersDispatch
}

export const useUpdateAnswersStatus = () => {
    const updateAnswersStatusDispatch = async (
        waitinglistrecordId: number,
        questionnaireresponseId: number
    ) => {
        try {
            const answersSubmitPayload = [
                {
                    op: 'replace',
                    path: '/StatusId',
                    value: 3,
                },
            ]

            const response = await fetch(
                Connection.baseUrl('QuestionnaireResponse') +
                    `waitinglistrecords/${waitinglistrecordId}/questionnaireresponses/${questionnaireresponseId}`,
                {
                    method: 'PATCH',
                    headers: Connection.headers(),
                    body: JSON.stringify(answersSubmitPayload),
                }
            )

            if (!response.ok) {
                throw new Error(
                    `Error: ${response.status} - ${response.statusText}`
                )
            }
        } catch (error) {
            console.error('Failed to fetch question answers:', error)
        }
    }

    return updateAnswersStatusDispatch
}

export default patientAnswerSlice.reducer
